<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  initial: { type: Object, default: () => null }, // null = creating, object = editing
});
const emit = defineEmits(['save', 'cancel']);

const ICONS = ['💬','📧','📞','💵','💰','🎁','📦','👰','🍼','📸','🎬','🏃','⏰','🎯','🔔','✨','🏷️','📅','🌐','⭐','🌸','🎂','💌','📍','✅','🚀','💼','🏪','🏆','🤝'];

const BACKEND_TYPES = [
  { key: 'msg_keyword',        label: 'Mensaje contiene palabras clave', backend: 'message_received', config_kind: 'keywords' },
  { key: 'msg_channel',        label: 'Mensaje recibido en canal específico', backend: 'message_received', config_kind: 'channel' },
  { key: 'msg_first',          label: 'Primer mensaje del cliente (cualquiera)', backend: 'message_received', config_kind: 'first' },
  { key: 'msg_first_in_stage', label: 'Primer mensaje del cliente en una etapa', backend: 'message_received', config_kind: 'first_in_stage' },
  { key: 'tag_added',          label: 'Etiqueta agregada al lead', backend: 'tag_added', config_kind: 'tag' },
];

const name      = ref('');
const icon      = ref('✨');
const typeKey   = ref('msg_keyword');
const keywords  = ref(''); // comma-separated
const channel   = ref('');  // for msg_channel
const tag       = ref('');  // for tag_added

watch(() => props.initial, (init) => {
  if (init) {
    name.value     = init.name || '';
    icon.value     = init.icon || '✨';
    typeKey.value  = init.typeKey || 'msg_keyword';
    keywords.value = (init.config?.keywords || []).join(', ');
    channel.value  = init.config?.channel || '';
    tag.value      = init.config?.tag || '';
  } else {
    name.value = ''; icon.value = '✨'; typeKey.value = 'msg_keyword';
    keywords.value = ''; channel.value = ''; tag.value = '';
  }
}, { immediate: true });

function selectedType() { return BACKEND_TYPES.find(t => t.key === typeKey.value); }

function save() {
  const t = selectedType();
  if (!name.value.trim() || !t) return;
  const cfg = {};
  if (t.config_kind === 'keywords') {
    cfg.keywords = keywords.value.split(',').map(s => s.trim()).filter(Boolean);
  } else if (t.config_kind === 'channel') {
    cfg.channel = channel.value.trim();
  } else if (t.config_kind === 'first') {
    cfg.first_message = true;
  } else if (t.config_kind === 'first_in_stage') {
    cfg.first_in_stage = true;
  } else if (t.config_kind === 'tag') {
    cfg.tag = tag.value.trim();
  }
  emit('save', {
    id: props.initial?.id,
    name: name.value.trim(),
    icon: icon.value,
    typeKey: typeKey.value,
    backend_type: t.backend,
    config: cfg,
  });
}
</script>

<template>
  <div class="bg-n-background border border-n-weak rounded-lg shadow-2xl p-3 space-y-2.5 w-[420px]">
    <div class="text-sm font-semibold text-n-slate-12">
      {{ initial ? 'Editar disparador personalizado' : 'Nuevo disparador personalizado' }}
    </div>

    <div>
      <label class="text-[10px] text-n-slate-11 block mb-0.5">Nombre</label>
      <input v-model="name" type="text" placeholder="Ej. Cliente confirmó el pago"
             class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-blue-400" />
    </div>

    <div>
      <label class="text-[10px] text-n-slate-11 block mb-0.5">Icono</label>
      <div class="grid grid-cols-10 gap-1">
        <button v-for="e in ICONS" :key="e" type="button"
                class="w-7 h-7 rounded text-base hover:bg-n-alpha-black1"
                :class="icon === e ? 'bg-blue-500/15 ring-1 ring-blue-400' : ''"
                @click="icon = e">{{ e }}</button>
      </div>
    </div>

    <div>
      <label class="text-[10px] text-n-slate-11 block mb-0.5">Tipo de disparador</label>
      <select v-model="typeKey"
              class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-background text-n-slate-12">
        <option v-for="t in BACKEND_TYPES" :key="t.key" :value="t.key">{{ t.label }}</option>
      </select>
    </div>

    <!-- Config dinámica según tipo -->
    <div v-if="typeKey === 'msg_keyword'">
      <label class="text-[10px] text-n-slate-11 block mb-0.5">
        Palabras clave (separadas por coma · matching insensible a acentos)
      </label>
      <input v-model="keywords" type="text" placeholder="pagué, pague, transferí, deposité"
             class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-blue-400" />
      <div class="text-[10px] text-n-slate-11 mt-1">
        💡 Cualquier mensaje del cliente que contenga al menos una de estas palabras dispara el bot
      </div>
    </div>

    <div v-else-if="typeKey === 'msg_channel'">
      <label class="text-[10px] text-n-slate-11 block mb-0.5">Canal</label>
      <select v-model="channel"
              class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-background text-n-slate-12">
        <option value="">— Cualquiera —</option>
        <option value="whatsapp">WhatsApp</option>
        <option value="instagram">Instagram</option>
        <option value="facebookpage">Facebook</option>
        <option value="email">Email</option>
        <option value="api">API genérica</option>
      </select>
    </div>

    <div v-else-if="typeKey === 'tag_added'">
      <label class="text-[10px] text-n-slate-11 block mb-0.5">Etiqueta específica (vacío = cualquiera)</label>
      <input v-model="tag" type="text" placeholder="ej. cliente-vip"
             class="w-full px-2 py-1.5 text-xs rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-blue-400" />
    </div>

    <div v-else-if="typeKey === 'msg_first'" class="text-[11px] text-n-slate-11 italic">
      Dispara solo en el primer mensaje incoming de la conversación (global, una sola vez por contacto).
    </div>

    <div v-else-if="typeKey === 'msg_first_in_stage'" class="text-[11px] text-n-slate-11 italic">
      Dispara la primera vez que el cliente escriba estando en una etapa del embudo.
      <strong>Recuerda agregar la condición "Etapa del embudo"</strong> abajo para indicar de qué etapa.
      Vuelve a poder disparar si el lead se mueve a otra etapa configurada.
    </div>

    <div class="flex justify-end gap-2 pt-1">
      <button type="button" class="text-[11px] text-n-slate-11 hover:text-n-slate-12 px-2 py-1"
              @click="emit('cancel')">Cancelar</button>
      <button type="button"
              class="text-[11px] text-white bg-blue-500 hover:bg-blue-600 px-3 py-1.5 rounded disabled:opacity-50"
              :disabled="!name.trim()"
              @click="save">
        {{ initial ? 'Guardar cambios' : 'Crear disparador' }}
      </button>
    </div>
  </div>
</template>
